import { useEffect, useId, useRef, useState, type RefObject } from 'react'
import { routeValidationErrors } from '../../api/validationError'
import { useFocusRequest } from '../../platform/a11y/useFocusRequest'
import { emit } from '../../platform/analytics/analytics'
import { Button } from '../../platform/primitives/Button'
import { Field, fieldErrorId } from '../../platform/primitives/Field'
import { Overlay } from '../../platform/overlay/Overlay'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { InviteLink } from './InviteLink'
import { INVITABLE_ROLES, ROLE_LABELS, type Role } from './roles'
import { HouseholdError } from './householdError'
import { isExpired, useCreateInvitation, type Invitation } from './useInvitations'
import type { Member } from './useMembers'
import './household.css'

/**
 * What each role actually gets, in the words a person choosing one needs.
 *
 * Transcribed from `roles.ts`'s own predicates rather than invented:
 * `canManageMembers` is owner-or-admin, `canEditTransactions` adds member, and
 * a viewer is left with reading. `owner` has no entry because it is not
 * offerable — see `INVITABLE_ROLES`.
 *
 * **No sentence here contains another role's LABEL.** `InviteDialog.test.tsx`
 * finds each radio by an accessible name matched loosely (`/Admin/`), and the
 * word "Owner" appearing in a description would make the test asserting owner
 * is never offered fail against a dialog that is correct. The descriptions are
 * wired as `aria-describedby` rather than sitting inside the `<label>`, which
 * keeps them out of the accessible NAME as well — belt and braces, since the
 * two halves of that argument fail differently.
 */
const ROLE_NOTES: Record<Exclude<Role, 'owner'>, string> = {
  admin: 'Can invite people and manage who has access.',
  member: 'Can add and edit transactions, accounts and budgets.',
  viewer: 'Can see everything, but cannot change anything.',
}

/**
 * Why this address cannot be invited, or `null`.
 *
 * **Both** of the server's guards are pre-empted here, because both round-trip
 * as a bare 400 whose `detail` string is the only thing distinguishing them:
 *
 *   400 "User is already a member of this household"
 *   400 "Pending invitation already exists for this email"
 *
 * The second is not in the PRD or the spec — it was found in
 * `invitation_service.py:102` during intake. Its absence is why M12-R10 spoke
 * only of members.
 *
 * Case is folded on both sides. iOS lowercases before sending
 * (`HouseholdUseCases.swift`), web does the same in `useCreateInvitation`, and
 * the server's pending check compares raw — so an unfolded client check would
 * let `BOB@x.com` through to create a second pending row for the same person.
 *
 * An `expired` or `revoked` invitation is deliberately NOT a duplicate: M12-R9
 * keeps them visible precisely so they can be re-sent.
 *
 * **The pending check runs `isExpired` (`useInvitations.ts`), not
 * `status === 'pending'` alone.** `cleanup_expired_invitations` (backend
 * `invitation_service.py:577`) is defined once and called from nowhere, so
 * `status` never actually transitions to `'expired'` in production — a row
 * past its `expiresAt` arrives as `'pending'` forever. Trusting `status`
 * alone here would block a re-invite the server would happily accept
 * (`invitation_service.py:97-98` only rejects a *live* pending invitation),
 * with this dialog reading "already pending" for a row the panel behind it
 * is labelling "Expired".
 */
export function inviteBlockedReason(
  email: string,
  members: Member[],
  invitations: Invitation[],
): string | null {
  const target = email.trim().toLowerCase()
  if (target === '') return null

  const member = members.find((m) => m.email?.toLowerCase() === target)
  if (member) {
    const who = member.displayName ?? member.email ?? 'That person'
    return `${who} is already a member of this household.`
  }

  const pending = invitations.find(
    (i) => i.email.toLowerCase() === target && i.status === 'pending' && !isExpired(i),
  )
  if (pending) return `An invitation to ${pending.email} is already pending.`

  return null
}

/**
 * The server failures worth putting ON the field rather than in a generic line.
 *
 * The 400 is already pre-empted by `inviteBlockedReason` against the lists
 * this screen holds — so reaching one here means the lists were stale: someone
 * else invited or added this person between the panel loading and Send being
 * pressed. That is a fact about the ADDRESS, which is why it lands on the email
 * field rather than in the dialog's own error line.
 *
 * **422 no longer has its own arm here.** It used to return a fixed
 * `'Enter a valid email address.'` for ANY 422, which is what issue #473
 * found: the server's real body is `{ detail: ValidationError[] }`, and on a
 * body of exactly two fields with the role picked from a radio group the
 * email genuinely is the one a user can get wrong most of the time — but
 * `submit`'s own catch now reads that body and routes every entry, so this
 * function is only ever consulted as the FALLBACK when a 422's body carries
 * no usable `detail`.
 */
function fieldMessageFor(status: number | undefined): string | null {
  if (status === 400) return 'That person has already been invited or is already a member.'
  return null
}

/** The one field on this form with its own error slot (`Field`'s `error`
 *  prop, wired to `serverField` below) — `role` has no error-display control
 *  of its own, so a 422 naming it lands at dialog level instead. */
const KNOWN_FIELDS = ['email'] as const

/**
 * The single source for the roles fieldset's visible `<legend>` text — read
 * by the JSX below AND by `describeOtherEntry`'s dialog copy, so renaming it
 * cannot leave the error line quoting the old word (architecture review
 * round 2, issue #473 non-blocking 4). A wire identifier is never shown bare
 * either way: a raw `loc` segment in front of a user is the same class of
 * defect this ticket fixes, one layer up.
 */
const FIELD_LABELS = { role: 'Role' } as const

/** One 422 entry with no field slot of its own on this form, as one line of
 *  dialog copy — labelled when `FIELD_LABELS` has an entry for its field,
 *  bare otherwise. */
function describeOtherEntry(entry: { field: string | undefined; message: string }): string {
  const label =
    entry.field !== undefined && entry.field in FIELD_LABELS
      ? FIELD_LABELS[entry.field as keyof typeof FIELD_LABELS]
      : undefined
  return label !== undefined ? `${label}: ${entry.message}` : entry.message
}

/** Everything else — a failure about the REQUEST rather than about the address. */
function dialogMessageFor(status: number | undefined): string {
  if (status === 403) return 'Your role does not allow inviting people to this household.'
  if (status === 404) return 'This household could not be found.'
  return 'The invitation could not be sent. Try again in a moment.'
}

/** `HouseholdError`'s own status, narrowed from `unknown` in one place — the
 *  same shape `MerchantForm.tsx`'s `statusOf` takes for the identical job. */
function statusOf(cause: unknown): number | undefined {
  return cause instanceof HouseholdError ? cause.status : undefined
}

/**
 * Send an invitation — M12-R7, and the task the module exists for.
 *
 * **Two states, one component.** The form, then the sent panel. Not two
 * dialogs: the second is the outcome of the first and closing between them
 * would throw away the one thing the user came for, which is the link.
 *
 * **On `Overlay`, not on `Dialog.tsx`'s two fixed shapes** — the same reasoning
 * `CategoryForm.tsx` and `MerchantForm.tsx` both give: a form is neither an
 * announcement nor a fixed one/two-button confirmation. `surface="glass"` is
 * safe here for the same reason it is safe there — this panel carries no
 * `--neg-soft` content, only fields, radios and buttons.
 *
 * **The lists arrive as props; this component fetches neither.** `Household.tsx`
 * already holds both — the member list it renders and the invitation list the
 * panel below it renders — and a presentational component does not fetch
 * (CLAUDE.md). It does own the WRITE, because the write has no other home.
 *
 * ## Why the copy-link is the delivery mechanism and not a convenience
 *
 * Issue 305: the backend's invitation email links to
 * `GET /invitations/{token}/open`, which 302s to
 * `expensemonitor://invite/accept?token=…` — a scheme nothing on a desktop has
 * registered. So an invitation sent from this browser is unacceptable from a
 * browser unless the sender hands over a `/invite/:token` URL themselves, and
 * `AcceptInvitation.tsx` exists to receive exactly that. The copy states this
 * plainly rather than implying the email will do: an invitation nobody can
 * accept looks identical, to the sender, to one that simply has not been
 * accepted yet.
 *
 * **`token` can still be `null`, and that branch is rendered rather than
 * assumed away.** The schema types it `string | null` and the whole link
 * depends on it; a success panel built around a link that is not there would
 * render "…/invite/null". That branch says the invitation was sent and that the
 * link is unavailable, which is the honest thing to say.
 *
 * **The link is shown as well as copied.** `navigator.clipboard` is
 * secure-context-only — it is simply absent on the plain-http LAN origin this
 * project uses for real-device testing — so a Copy button is not on its own a
 * way to get the link out of the page. The read-only field is what makes the
 * link recoverable when the clipboard is not there, and the button's failure
 * arm points at it.
 */
export function InviteDialog({
  open,
  householdId,
  members,
  invitations,
  onClose,
  restoreFocusTo,
}: {
  open: boolean
  householdId: string
  members: Member[]
  invitations: Invitation[]
  onClose: () => void
  /**
   * Where focus lands when this closes and the control that opened it is gone.
   *
   * Required, though `Overlay` types it optional: the empty state's "Invite
   * someone" chip is one of the two openers, and a successful send invalidates
   * the invitations key — so the list becomes non-empty, the chip unmounts, and
   * it no longer exists by the time `Done` is pressed. `Overlay` would then fall
   * back to the content region (`<main>`), which is only the floor. The page's
   * Invite button is the better landing — the same place the user started — so
   * the caller must name it rather than silently settle for `<main>`. jsdom does
   * not blur removed nodes, so no unit test here can see the difference.
   */
  restoreFocusTo: RefObject<HTMLElement | null>
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [serverField, setServerField] = useState<string | null>(null)
  /** One or more lines for the dialog's own error region — never joined into
   *  a single string, so a 422 with more than one non-field entry can render
   *  each on its own line (`otherEntries.map(describeOtherEntry)` below)
   *  rather than one run-on sentence. `null` means no failure. */
  const [failure, setFailure] = useState<string[] | null>(null)
  const [sent, setSent] = useState<Invitation | null>(null)
  const { create, isPending } = useCreateInvitation(householdId)
  const noteIds = useId()
  /**
   * Where focus goes when the form is replaced by the sent panel.
   *
   * Sending unmounts the very button that was focused, and a browser then
   * blurs to `<body>` — which is outside this panel, so the trap's next Tab
   * walks into the background document the overlay is hiding. That is the
   * exact fault `useFocusRequest` exists for, and its docblock's own warning
   * applies here: jsdom does not blur a removed node, so the defect is
   * invisible to a suite that only checks what rendered. The heading is the
   * one element the sent panel always has, and landing at the top of the thing
   * that just replaced the form is also what announces it.
   */
  const sentHeadingRef = useRef<HTMLHeadingElement>(null)
  /**
   * Where focus goes when a submit is REFUSED — issue 386.
   *
   * `aria-disabled` is not `disabled`: the button still takes the click and the
   * form still submits, so `submit()`'s guard runs and, before this card,
   * returned in silence. Native `disabled` at least made a refusal unambiguous
   * by not reacting at all; nothing replaced that when #373 took it away.
   * Landing on the field that has to change is the reply — it names the thing
   * to fix, and on the blocked path it moves focus to the very control whose
   * description now carries the reason.
   */
  const emailRef = useRef<HTMLInputElement>(null)
  const focus = useFocusRequest({ sentHeading: sentHeadingRef, email: emailRef })
  // `'form'`, matching `MerchantForm`'s own type segment — the same density as
  // the `Field` this fieldset sits beside.
  const rowHeight = useHitTarget('form')

  // Explicit, rather than letting `Field` mint its own: the submit button below
  // has to name the error element, and `fieldErrorId` can only derive an id
  // that is known on both sides.
  const emailId = useId()
  const blocked = inviteBlockedReason(email, members, invitations)
  // The client's own reason wins over a stale server one: the server message
  // describes the address as it was when Send was pressed, and the user is
  // typing a different one now.
  const fieldError = blocked ?? serverField ?? undefined

  /**
   * Task 12's `invite_blocked_duplicate`, edge-triggered the same way
   * `Async`'s own `announce` is (`Async.tsx`'s `previous` ref) — on the
   * transition INTO blocked, not on every keystroke a blocked address stays
   * blocked for. No props: the reason itself (which member, which pending
   * row) is exactly the thing `vocabulary.ts` refuses to carry.
   *
   * **Tracks the REASON, not a blocked/unblocked boolean** — fix round 2
   * (issue 319 blocking finding B2). A boolean cannot tell "still blocked,
   * same address" (must stay silent — a role radio click re-renders this
   * with nothing new to report) from "still blocked, a DIFFERENT address"
   * (a genuine second instance of the thing this event measures, most
   * reachable by pasting a second already-taken address over the first,
   * with no unblocked render in between). Decided deliberately, against the
   * shipped behaviour: a second block on a different address must NOT be
   * silent, because each is a distinct moment the user tried an address
   * that could not be invited, and collapsing two of those into one undercounts
   * exactly the friction this event exists to surface.
   *
   * `blockedRef` holds `inviteBlockedReason`'s own return value — a sentence
   * naming the blocking member or invitation — but it never reaches `emit`;
   * it is compared locally, the same as `email` state itself, and the event
   * still carries no props.
   */
  const blockedRef = useRef<string | null>(null)
  useEffect(() => {
    if (blocked !== null && blocked !== blockedRef.current) emit('invite_blocked_duplicate')
    blockedRef.current = blocked
  }, [blocked])

  async function submit() {
    const trimmed = email.trim()
    // The in-flight arm is checked FIRST and answers differently. It is not a
    // validity refusal: the user pressed a valid Send and is standing on the
    // button, so moving focus off it mid-request would be #373's own defect in
    // the other direction. `aria-busy` is what states that arm.
    if (isPending) return
    if (trimmed === '' || blocked !== null) {
      focus('email')
      return
    }
    setServerField(null)
    setFailure(null)
    try {
      const invitation = await create(trimmed, role)
      setSent(invitation)
      // Task 12. `role` is the one picked in the form — an enum, never the
      // address just sent.
      emit('invite_sent', { role })
      focus('sentHeading')
    } catch (cause) {
      const status = statusOf(cause)
      const onField = fieldMessageFor(status)
      if (onField !== null) {
        setServerField(onField)
        return
      }
      // A 422 is FastAPI's own genuine validation failure — issue #473. The
      // real body is `{ detail: ValidationError[] }`, never a bare string,
      // and EVERY entry is surfaced: one routed onto the Email field when
      // its `loc` names it, the rest each becoming its own line in the
      // dialog's own error region — labelled where this form has a label
      // for the field (`FIELD_LABELS`) and bare otherwise, never the wire's
      // own field name. Falls through to `dialogMessageFor`'s generic copy
      // only when the body carries no usable `detail` at all.
      if (status === 422) {
        const body = cause instanceof HouseholdError ? cause.body : undefined
        const { fieldMessages, otherEntries } = routeValidationErrors(body, KNOWN_FIELDS)
        const emailMessages = fieldMessages.get('email')
        if (emailMessages !== undefined || otherEntries.length > 0) {
          if (emailMessages !== undefined) setServerField(emailMessages.join('; '))
          if (otherEntries.length > 0) setFailure(otherEntries.map(describeOtherEntry))
          return
        }
      }
      setFailure([dialogMessageFor(status)])
    }
  }

  return (
    <Overlay
      open={open}
      onClose={onClose}
      surface="glass"
      // M12-R19: a full-screen sheet on a phone, a centred dialog everywhere
      // else. Opt-in, so no other Overlay caller's geometry moves.
      placement="sheet"
      label="Invite someone"
      restoreFocusTo={restoreFocusTo}
    >
      {sent === null ? (
        <form
          className="invite-dialog"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <h3 className="invite-dialog__title">Invite someone</h3>
          <p className="invite-dialog__note">
            They will get access to this household&rsquo;s accounts, transactions and budgets.
          </p>

          <Field
            id={emailId}
            ref={emailRef}
            label="Email"
            type="email"
            autoComplete="email"
            size="form"
            placeholder="name@example.com"
            value={email}
            error={fieldError}
            onChange={(e) => {
              setEmail(e.target.value)
              setServerField(null)
              setFailure(null)
            }}
          />

          {/* A `<fieldset>`/`<legend>` rather than a `div` with
              `role="radiogroup"` and an `aria-label`: the legend is a VISIBLE
              name for the group, which `MerchantForm`'s pattern has to supply
              twice (an `aria-hidden` span for sight, an `aria-label` for the
              tree). One element, both jobs, and it is the native grouping
              element for exactly this. */}
          <fieldset className="invite-dialog__roles">
            <legend className="invite-dialog__legend">{FIELD_LABELS.role}</legend>
            {INVITABLE_ROLES.map((r) => {
              // `INVITABLE_ROLES` is typed `readonly Role[]`, which includes
              // `owner` — the value it deliberately never contains. Narrowed
              // rather than indexed with a cast so `ROLE_NOTES`, which has no
              // `owner` entry, stays exhaustive by type.
              if (r === 'owner') return null
              const noteId = `${noteIds}-${r}`
              return (
                <div key={r} className="invite-dialog__option" style={{ minHeight: rowHeight }}>
                  <label className="invite-dialog__option-label">
                    {/* The real radio, stretched invisibly over the whole
                        option — the same mechanism `merchant-form__input` and
                        `category-form__input` document, and for the same
                        reason: `e2e/hitTargets.mjs` measures every
                        `input:not([type=hidden])`, and a UA-drawn radio is
                        about 13px against a floor of 33 (44 below 640px). It
                        also settles the OTHER hazard a native control carries
                        — `styles/nativeAccent.test.ts`: an unstyled radio
                        paints the platform's system blue, a colour belonging
                        to no palette and visible to no guard, because the
                        defect is the ABSENCE of a declaration. Invisible is
                        that file's own second answer, alongside
                        `accent-color`, and the one that fixes both problems
                        at once.

                        `inset: 0` resolves against `.invite-dialog__option`,
                        not this label — the label is `position: static` — so
                        the target covers the description line too. */}
                    <input
                      className="invite-dialog__input"
                      type="radio"
                      name="invite-role"
                      value={r}
                      checked={role === r}
                      aria-describedby={noteId}
                      onChange={() => setRole(r)}
                    />
                    {ROLE_LABELS[r]}
                  </label>
                  {/* Outside the `<label>`, so it is the radio's DESCRIPTION
                      and not part of its name — see `ROLE_NOTES`. */}
                  <span id={noteId} className="invite-dialog__option-note">
                    {ROLE_NOTES[r]}
                  </span>
                </div>
              )
            })}
          </fieldset>

          {/* Its own live region: this arrives after the dialog opened, and a
              `dialog` announces its contents on open only. Same shape
              `ConfirmDialog`'s failure line takes for the single-line case.
              A 422 can carry more than one non-field entry (issue #473) — a
              `<ul>` when there is more than one line is the presentation for
              that, rather than joining every entry into one run-on sentence.
              `role="alert"` sits on a plain wrapping `<div>`, never the
              `<ul>` itself — architecture review round 2: `role="alert"`
              overrides a `<ul>`'s own implicit `list` role, so its `<li>`s
              would sit in no list and real assistive tech would not announce
              them as items. The wrapper keeps the region's role and the
              list's semantics both intact. Keyed on INDEX, deliberately, not
              the line's own text: `error-422-validation-multiple.json` is
              the proof two entries can carry identical text (`page` and
              `page_size`, same `msg`), which a text key would collide on. */}
          {failure !== null && failure.length === 1 && (
            <p className="invite-dialog__error" role="alert">
              {failure[0]}
            </p>
          )}
          {failure !== null && failure.length > 1 && (
            <div className="invite-dialog__error" role="alert">
              <ul>
                {failure.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="invite-dialog__actions">
            <Button type="button" variant="chip" size="form" onClick={onClose}>
              Cancel
            </Button>
            {/* The page body's one lime fill while this is up, and the sole
                reason the user opened it. Unavailable on the client's own
                duplicate check as well as while in flight — the reason is on
                the field, which is where M12-R10 asks for it.

                **Never native `disabled` — issue 373.** Disabling the focused
                element moves focus to `<body>` in Chromium and nothing
                restores it. That is the third time this defect has been fixed
                on this screen (`InvitationRow`'s resend, #362; `RoleMenu`'s
                trigger, #312 blocker B2) and the worst landing of the three:
                this button sits inside a focus trap, and `<body>` is OUTSIDE
                it, so the recovery path is worse here than on an ordinary
                page rather than better.

                It is NOT a copy of #362's fix, which is why it was carded
                separately. There, `disabled` meant only "in flight", so
                deleting it lost nothing the handler's own guard did not
                already give. Here it carried three conditions, and two of
                them are VALIDITY — for which `disabled` is normally the
                correct affordance.

                **Both kinds move to `aria-disabled` anyway, and the card's
                objection to that turned out to be answered already.** It
                predicted that dropping `isPending` alone would reintroduce
                the drop by the validity route — the user clears the email
                field while focus is on this button. Measured in a browser:
                that route was ALREADY live, because `disabled` already
                carried validity. So this is strictly a repair, not a trade.

                Three properties keep it honest without the native attribute:
                `aria-busy` states the in-flight half; `submit()`'s own early
                return refuses re-entry (it predates this card); and
                `.btn[aria-disabled='true']` in `primitives.css` is dimmed AND
                dashed — the dash being M0-R11's required non-colour signal,
                and the ink rung chosen to clear the full 4.5 an operable
                control owes. */}
            <Button
              type="submit"
              variant="primary"
              size="form"
              aria-busy={isPending}
              aria-disabled={blocked !== null || email.trim() === '' || undefined}
              // Issue 386. The duplicate reason is rendered on the field and
              // was referenced by nothing — a screen-reader user reaching this
              // button heard "unavailable" and no more, with the sentence
              // explaining why three elements away.
              //
              // **Bound on `blocked` only, and the empty-email condition is
              // deliberately left unexplained.** There is no reason text for it
              // to point at, and the only place to put one is `Field`'s `error`
              // — whose PRESENCE is what marks a field invalid, so inventing a
              // sentence would paint `aria-invalid` and the error skin on a
              // form nobody has touched yet. An empty required field is not a
              // mistake the user has made. That gap is also what keeps
              // `primitives.css`'s `.btn[aria-disabled='true']` its witness
              // that the dashed skin must not lean on a bound reason.
              aria-describedby={blocked !== null ? fieldErrorId(emailId) : undefined}
            >
              {isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="invite-dialog">
          {/* `tabIndex={-1}` makes it focusable without putting it in the tab
              order — `Security.tsx`'s own heading takes the same pair. */}
          <h3 className="invite-dialog__title" tabIndex={-1} ref={sentHeadingRef}>
            Invitation sent to {sent.email}
          </h3>
          {/* One implementation of this, shared with `InvitationRow`'s
              post-resend state — see `InviteLink`'s docblock. It owns the
              clipboard handling, the secure-context fallback and the
              `token === null` branch that used to live here. */}
          <InviteLink
            token={sent.token}
            unavailable="The invitation is on its way by email. No shareable link came back with it, so this one can only be accepted from the mobile app."
          >
            <p className="invite-dialog__note">
              The emailed link only works in the mobile app — send this link instead.
            </p>
          </InviteLink>
          <div className="invite-dialog__actions">
            <Button type="button" variant="primary" size="form" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Overlay>
  )
}
